import '../utils/shims';
import React from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { datadogRum } from '@datadog/browser-rum';
import { CookiesProvider, Cookies } from 'react-cookie';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { renderRoutes } from 'react-router-config';
import { BrowserRouter } from 'react-router-dom';

import appSettings, { setConfig } from 'wanderu-config';
import {
    fetchTestsAction,
    initializeVisitorId,
} from 'wanderu-ducks-split-test';
import Logger from 'wanderu-logger';
import { SnowplowService, initUserEvents, getFacebookPixelCookies } from 'wanderu-service-analytics';
import WapiService, {
    injectStore as injectWapiServiceWithStore,
    initService as initWapiService,
} from 'wanderu-service-wapi';
import {
    deserialize,
    getQueryParamsFromUrl,
} from 'wanderu-utils';

import baseAppConfig from '../config/baseApp.locales';
import App, { hydrate as hydrateApp } from '../containers/App';
import initialRoutes from '../routes';
import configureStore from '../utils/configureStore';
import { polyfillIntlWithLocales } from '../utils/intlPolyfill';

const logger = Logger.getLogger('client');

function render(routes, store, cookies) {
    hydrateRoot(
        document.getElementById('root'),
        <BrowserRouter>
            <CookiesProvider cookies={cookies}>
                <Provider store={store}>
                    {renderRoutes(routes)}
                </Provider>
            </CookiesProvider>
        </BrowserRouter>,
    );
}

async function init() {
    const windowSettings = deserialize(window.appSettings);
    setConfig({
        config: windowSettings.config,
        env: {
            ...windowSettings.env,
            isClient: true,
            isServer: false,
        },
    });

    Logger.config.useConfig(appSettings.config.logging.client);
    Logger.config.addMetadata('build.version', () => appSettings.env.build.version);
    Logger.config.addMetadata('build.region', () => appSettings.env.build.region);
    Logger.config.addMetadata('build.hash', () => appSettings.env.build.gitHash);
    Logger.config.addMetadata('userAgent', () => navigator.userAgent);
    Logger.config.addMetadata('cookiesEnabled', () => navigator.cookieEnabled);
    Logger.config.addMetadata('url', () => window.location.href);
    Logger.config.addMetadata('app', () => 'ui-react (client)');
    Logger.config.addMetadata('snowplowIds', () => ({
        networkUserId: SnowplowService.getNetworkUserId(),
        ...SnowplowService.getDomainUserInfo(),
    }));

    initAmplitude();

    datadogRum.init({
        applicationId: '698261cf-4a16-4573-8912-599d76ad9094',
        clientToken: 'pub2a003a53cce099fd6639762628a1a492',
        defaultPrivacyLevel: 'mask-user-input',
        enableExperimentalFeatures: ['clickmap'],
        env: appSettings.env.wanderuEnv,
        service: 'ui-react',
        sessionReplaySampleRate: 2,
        sessionSampleRate: 2,
        site: 'datadoghq.com',
        trackFrustrations: true,
        trackInteractions: true,
        trackUserInteractions: true,
        trackViewsManually: true,
        version: appSettings.env.build.version,
    });

    window.addEventListener('pagehide', Logger.sendBeacon);
    window.onerror = function handleError(message, url, line, col, error) {
        if (message.toLowerCase() === 'script error.' || message.toLowerCase().includes('can\'t find variable: $') || message.toLowerCase().includes('$ is not defined')) {
            logger.warn('uncaughtError', message, { line, stack: error.stack, url });
        } else {
            logger.error('uncaughtError', message, { line, stack: error.stack, url });
        }

        return false;
    };

    // eslint-disable-next-line no-underscore-dangle
    const appState = deserialize(window.__INITIAL_STATE__);
    const store = configureStore(appState);
    const cookies = new Cookies();
    const visitorId = initializeVisitorId(cookies);
    initSnowplowAnalytics(store);
    initWapiService({ visitorId });
    await initSplitTestData(store, visitorId);

    // Check if polyfill required
    if (!window.Intl) {
        const locales = Object.values(baseAppConfig.localeMap);
        await polyfillIntlWithLocales(locales);
    }

    // eslint-disable-next-line no-underscore-dangle
    const i18nData = deserialize(window.__INITIAL_I18N__);

    hydrateApp(i18nData);
    await App.clientFetchData(i18nData);

    render(initialRoutes, store, cookies);
}

async function initSplitTestData(store, visitorId) {
    if (['partner/header', 'partner/footer'].some((page) => document.location.pathname.includes(page))) {
        return;
    }

    await store.dispatch(fetchTestsAction.fetchTests({
        path: document.location.pathname,
        queryParams: getQueryParamsFromUrl(document.location.search),
        wanderuVisitorId: visitorId,
    }));
    injectWapiServiceWithStore(store);
}

async function initAmplitude() {
    try {
        if (appSettings?.config?.amplitude?.apiKey) {
            amplitude.init(appSettings.config.amplitude.apiKey, { // Browser Analytics https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#configure-the-sdk
                appVersion: appSettings.env.build.version,
                autocapture: {
                    attribution: {
                        resetSessionOnNewCampaign: true,
                    },
                    elementInteractions: true,
                    fileDownloads: true,
                    formInteractions: true,
                    pageViews: true,
                    sessions: true,
                },
                instanceName: 'ui-react',
                serverUrl: appSettings.config.amplitude.serverUrl,
            });
        }
    } catch {
        logger.error('amplitudeInitializationFailed');
    }
}

async function initSnowplowAnalytics(store) {
    const { region, version, gitHash } = appSettings.env.build;
    try {
        initUserEvents(store);
        SnowplowService.init(region, version, gitHash);
        SnowplowService.fetchSDK(onSnowplowLoad);
        SnowplowService.createNewTracker('ui_react');
        SnowplowService.startTracking();
    } catch {
        logger.error('snowplowTrackingFailed');
    }
}

async function onSnowplowLoad() {
    WapiService.startNewSession(undefined, { preserveSessionId: true });
    const facebookPixelCookies = await getFacebookPixelCookies();
    if (!facebookPixelCookies) {
        return;
    }

    SnowplowService.fireStructuredEvent({
        action: 'loaded',
        category: 'facebook_pixel',
        property: {
            fbc: facebookPixelCookies.fbc,
            fbp: facebookPixelCookies.fbp,
        },
    });
}

init();
